import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Grid, GridColumn, GridRowClickEvent, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { orderBy, SortDescriptor } from '@progress/kendo-data-query'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { Button } from '@progress/kendo-react-buttons'
import { ComboBox, DropDownList } from '@progress/kendo-react-dropdowns'
import { Input, NumericTextBox, Switch, TextArea } from '@progress/kendo-react-inputs'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { FaChevronLeft, FaPlus, FaPoundSign } from 'react-icons/fa'
import {
  allocatePiggyBankItem,
  createPiggyBankItem,
  deletePiggyBankItem,
  getPiggyBankItems,
  getPiggyBankUser,
  getPiggyBankUsers,
  PiggyBankEntry,
  PiggyBankItem,
  PiggyBankUser,
  PiggyBankUserDetail,
  recordPiggyBankPayment,
  removePiggyBankAllocation,
  removePiggyBankPayment,
  updatePiggyBankItem,
} from '../../data/piggyBankData'
import { getEmployeeDropdown } from '../../data/employeeData'
import { dropDown } from '../../data/interfaces'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import PiggyBankTrends from './PiggyBankTrends'
import '../Lookups/Lookups.css'
import './PiggyBank.css'

const money = (value: number) =>
  `£${(Number(value) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const shortDate = (value: string | null) => {
  if (!value) return ''
  const d = new Date(value)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const openInNewTab = (path: string) => window.open(path, '_blank')

/** yyyy-MM-dd for the local day, so a date picked isn't shifted a day by UTC. */
const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Who the Give item dialog is for, if it's open. Null employee: pick one in the dialog. */
interface GiveTarget {
  employee: dropDown | null
}

/**
 * Everyone's piggy banks: what each person has earned, what they've been paid
 * and what they're still owed, where every entry came from, and the catalogue
 * of items that can be given to people.
 *
 * "Giving an item" adds to what someone has earned; "recording a payment"
 * notes money actually paid out to them, and comes off what they're owed.
 */
const PiggyBankAdmin = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [tab, setTab] = useState(0)

  const [users, setUsers] = useState<PiggyBankUser[] | null>(null)
  const [items, setItems] = useState<PiggyBankItem[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'balance', dir: 'desc' }])

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<PiggyBankUserDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [giving, setGiving] = useState<GiveTarget | null>(null)
  const [paying, setPaying] = useState<PiggyBankUserDetail | null>(null)
  const [editingItem, setEditingItem] = useState<PiggyBankItem | 'new' | null>(null)

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await getPiggyBankUsers())
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "The piggy banks couldn't be loaded.").message)
    }
  }, [])

  const loadItems = useCallback(async () => {
    try {
      setItems(await getPiggyBankItems())
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "The catalogue couldn't be loaded.").message)
    }
  }, [])

  const loadDetail = useCallback(
    async (employeeId: number) => {
      setDetailLoading(true)
      try {
        setDetail(await getPiggyBankUser(employeeId))
      } catch (error) {
        showToast(getApiErrorDetails(error, "Their piggy bank couldn't be loaded.").message, 'error')
      } finally {
        setDetailLoading(false)
      }
    },
    [showToast]
  )

  useEffect(() => {
    loadUsers()
    loadItems()
  }, [loadUsers, loadItems])

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null)
      return
    }
    loadDetail(selectedId)
  }, [selectedId, loadDetail])

  // Deleted people are hidden unless asked for - their piggy banks are kept,
  // just out of the way. The headline count and total follow the same rule so
  // they always describe the list on screen.
  const listedUsers = useMemo(
    () => (users ?? []).filter((u) => showDeleted || !u.isDeleted),
    [users, showDeleted]
  )

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = listedUsers.filter((u) => !term || u.employeeName.toLowerCase().includes(term))
    return orderBy(filtered, sort)
  }, [listedUsers, search, sort])

  const grandTotal = listedUsers.reduce((sum, u) => sum + u.total, 0)
  const grandBalance = listedUsers.reduce((sum, u) => sum + u.balance, 0)

  // Hiding deleted people shouldn't leave one of them open beside a list
  // that no longer shows them.
  useEffect(() => {
    if (showDeleted || selectedId === null) return
    if (users?.find((u) => u.employeeId === selectedId)?.isDeleted) setSelectedId(null)
  }, [showDeleted, selectedId, users])

  // After giving an item or recording a payment, refresh the list and show
  // the person it was for.
  const onSaved = async (updated: PiggyBankUserDetail) => {
    setGiving(null)
    setPaying(null)
    setTab(0)
    setSelectedId(updated.employeeId)
    setDetail(updated)
    await Promise.all([loadUsers(), loadItems()])
  }

  const removeEntry = async (entry: PiggyBankEntry) => {
    if (!detail) return
    const question = entry.isPayment
      ? `Remove the ${money(entry.value)} payment to ${detail.employeeName} on ${shortDate(entry.date)}? They'll be owed it again.`
      : `Remove ${entry.title} (${money(entry.value)}) from ${detail.employeeName}'s piggy bank?`
    if (!window.confirm(question)) return
    try {
      if (entry.isPayment) {
        await removePiggyBankPayment(entry.id)
      } else {
        await removePiggyBankAllocation(entry.id)
      }
      showToast('Removed', 'success')
      await Promise.all([loadDetail(detail.employeeId), loadUsers(), loadItems()])
    } catch (error) {
      showToast(getApiErrorDetails(error, "It couldn't be removed.").message, 'error')
    }
  }

  const removeItem = async (item: PiggyBankItem) => {
    const given =
      item.allocationCount > 0
        ? ` The ${plural(item.allocationCount, 'allocation')} already made will keep it and still count.`
        : ''
    if (!window.confirm(`Remove "${item.name}" from the catalogue? It can't be given to anyone after this.${given}`)) return
    try {
      await deletePiggyBankItem(item.piggyBankItemId)
      showToast(`Removed ${item.name}`, 'success')
      await loadItems()
    } catch (error) {
      showToast(getApiErrorDetails(error, "It couldn't be removed.").message, 'error')
    }
  }

  const liveItems = items ?? []

  return (
    <div className='lk-page'>
      <div className='lk-inner'>
        <button type='button' className='lk-crumb' onClick={() => navigate('/management')}>
          <FaChevronLeft /> Management
        </button>
        <h1 className='lk-title'>Piggy Bank</h1>
        <p className='lk-lead'>
          What everyone has earned in their piggy bank - referrals they raised that became jobs, and items from the
          catalogue given to them here - what they've been paid, and what they're still owed.
        </p>

        {loadError ? (
          <div className='lk-error'>{loadError}</div>
        ) : (
          <TabStrip selected={tab} onSelect={(e) => setTab(e.selected)}>
            <TabStripTab title='People'>
              <div className='pb-toolbar'>
                <Input
                  value={search}
                  onChange={(e) => setSearch(String(e.value ?? ''))}
                  placeholder='Search by name'
                  style={{ width: '240px' }}
                />
                <label className='pb-toolbar__toggle'>
                  <Switch checked={showDeleted} onChange={(e) => setShowDeleted(e.value)} />
                  Show deleted
                </label>
                <span className='pb-toolbar__total'>
                  {users
                    ? `${plural(listedUsers.length, 'person')} · ${money(grandTotal)} earned · ${money(grandBalance)} owed`
                    : ''}
                </span>
                <Button
                  themeColor='primary'
                  onClick={() => setGiving({ employee: null })}
                  disabled={liveItems.length === 0}
                  title={liveItems.length === 0 ? 'Add an item to the catalogue first' : undefined}
                >
                  <FaPlus style={{ marginRight: '0.4rem' }} />
                  Give item
                </Button>
              </div>

              {!users ? (
                <p className='lk-muted'>Loading…</p>
              ) : users.length === 0 ? (
                <p className='lk-muted'>Nobody has anything in their piggy bank yet.</p>
              ) : (
                <div className='pb-layout'>
                  <Grid
                    data={visibleUsers.map((u) => ({ ...u, selected: u.employeeId === selectedId }))}
                    selectedField='selected'
                    sortable
                    sort={sort}
                    onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
                    onRowClick={(e: GridRowClickEvent) => setSelectedId(e.dataItem.employeeId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <GridColumn
                      field='employeeName'
                      title='Name'
                      cell={(p) => (
                        <td>
                          {p.dataItem.employeeName}
                          {p.dataItem.isDeleted && <span className='pb-tag'>Deleted</span>}
                        </td>
                      )}
                    />
                    <GridColumn
                      field='referralTotal'
                      title='Referrals'
                      width='130px'
                      headerClassName='pb-num'
                      cell={(p) => (
                        <td className='pb-num'>
                          {money(p.dataItem.referralTotal)}
                          <div className='pb-sub'>{plural(p.dataItem.referralCount, 'referral')}</div>
                        </td>
                      )}
                    />
                    <GridColumn
                      field='allocationTotal'
                      title='Items'
                      width='130px'
                      headerClassName='pb-num'
                      cell={(p) => (
                        <td className='pb-num'>
                          {money(p.dataItem.allocationTotal)}
                          <div className='pb-sub'>{plural(p.dataItem.allocationCount, 'item')}</div>
                        </td>
                      )}
                    />
                    <GridColumn
                      field='total'
                      title='Earned'
                      width='120px'
                      headerClassName='pb-num'
                      cell={(p) => <td className='pb-num'>{money(p.dataItem.total)}</td>}
                    />
                    <GridColumn
                      field='paidTotal'
                      title='Paid'
                      width='120px'
                      headerClassName='pb-num'
                      cell={(p) => (
                        <td className='pb-num'>
                          {money(p.dataItem.paidTotal)}
                          {p.dataItem.lastPaidDate && (
                            <div className='pb-sub'>Last {shortDate(p.dataItem.lastPaidDate)}</div>
                          )}
                        </td>
                      )}
                    />
                    <GridColumn
                      field='balance'
                      title='Owed'
                      width='120px'
                      headerClassName='pb-num'
                      cell={(p) => (
                        <td className={`pb-num pb-strong${p.dataItem.balance > 0 ? ' pb-owed' : ''}`}>
                          {money(p.dataItem.balance)}
                        </td>
                      )}
                    />
                    <GridColumn
                      field='lastEntryDate'
                      title='Last entry'
                      width='120px'
                      cell={(p) => <td>{shortDate(p.dataItem.lastEntryDate)}</td>}
                    />
                  </Grid>

                  <section className='lk-card pb-detail'>
                    {selectedId === null ? (
                      <p className='lk-muted'>Pick someone to see what's in their piggy bank and where it came from.</p>
                    ) : detailLoading && !detail ? (
                      <p className='lk-muted'>Loading…</p>
                    ) : detail ? (
                      <PiggyBankDetail
                        detail={detail}
                        canGive={liveItems.length > 0 && !detail.isDeleted}
                        onGive={() =>
                          setGiving({ employee: { text: detail.employeeName, value: detail.employeeId } as dropDown })
                        }
                        onPay={() => setPaying(detail)}
                        onRemove={removeEntry}
                      />
                    ) : null}
                  </section>
                </div>
              )}
            </TabStripTab>

            <TabStripTab title='Trends'>
              <PiggyBankTrends users={users ?? []} />
            </TabStripTab>

            <TabStripTab title='Catalogue'>
              <div className='pb-toolbar'>
                <span className='lk-hint' style={{ margin: 0 }}>
                  Items that can be given to people. The value is what each one given starts at, and can be changed each
                  time it's given.
                </span>
                <Button themeColor='primary' onClick={() => setEditingItem('new')}>
                  <FaPlus style={{ marginRight: '0.4rem' }} />
                  Add item
                </Button>
              </div>

              {!items ? (
                <p className='lk-muted'>Loading…</p>
              ) : items.length === 0 ? (
                <p className='lk-muted'>The catalogue is empty. Add an item to start giving them to people.</p>
              ) : (
                <Grid data={items}>
                  <GridColumn field='name' title='Name' width='220px' />
                  <GridColumn field='description' title='Description' />
                  <GridColumn
                    field='value'
                    title='Value'
                    width='110px'
                    headerClassName='pb-num'
                    cell={(p) => <td className='pb-num'>{money(p.dataItem.value)}</td>}
                  />
                  <GridColumn
                    field='allocationCount'
                    title='Times given'
                    width='110px'
                    headerClassName='pb-num'
                    cell={(p) => <td className='pb-num'>{p.dataItem.allocationCount}</td>}
                  />
                  <GridColumn
                    title='Actions'
                    width='180px'
                    cell={(p) => (
                      <td className='k-command-cell'>
                        <Button themeColor='primary' onClick={() => setEditingItem(p.dataItem)}>
                          Edit
                        </Button>
                        <Button themeColor='error' style={{ marginLeft: '8px' }} onClick={() => removeItem(p.dataItem)}>
                          Remove
                        </Button>
                      </td>
                    )}
                  />
                </Grid>
              )}
            </TabStripTab>
          </TabStrip>
        )}
      </div>

      {giving && (
        <GiveItemDialog
          employee={giving.employee}
          items={liveItems}
          onClose={() => setGiving(null)}
          onSaved={onSaved}
        />
      )}

      {paying && <RecordPaymentDialog detail={paying} onClose={() => setPaying(null)} onSaved={onSaved} />}

      {editingItem && (
        <ItemDialog
          item={editingItem === 'new' ? null : editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={async (name) => {
            setEditingItem(null)
            showToast(editingItem === 'new' ? `Added ${name}` : `Saved ${name}`, 'success')
            await loadItems()
          }}
        />
      )}
    </div>
  )
}

// --- One person's piggy bank -----------------------------------------------------

const TYPE_LABELS: Record<PiggyBankEntry['type'], string> = {
  Referral: 'Referral',
  Allocation: 'Item',
  Payment: 'Paid',
}

const PiggyBankDetail = ({
  detail,
  canGive,
  onGive,
  onPay,
  onRemove,
}: {
  detail: PiggyBankUserDetail
  canGive: boolean
  onGive: () => void
  onPay: () => void
  onRemove: (entry: PiggyBankEntry) => void
}) => (
  <>
    <div className='pb-detail__head'>
      <h2 className='lk-subtitle' style={{ margin: 0 }}>
        {detail.employeeName}
        {detail.isDeleted && <span className='pb-tag'>Deleted</span>}
      </h2>
      <div className='pb-detail__actions'>
        {canGive && (
          <Button onClick={onGive}>
            <FaPlus style={{ marginRight: '0.4rem' }} />
            Give item
          </Button>
        )}
        {/* Deleted people can still be paid - a leaver may be owed what they earned. */}
        <Button
          themeColor='primary'
          onClick={onPay}
          disabled={detail.balance <= 0}
          title={detail.balance <= 0 ? "They aren't owed anything" : undefined}
        >
          <FaPoundSign style={{ marginRight: '0.4rem' }} />
          Record payment
        </Button>
      </div>
    </div>

    <div className='pb-figures'>
      <div className='pb-figure'>
        <div className='pb-figure__label'>Earned</div>
        <div className='pb-figure__value'>{money(detail.total)}</div>
      </div>
      <div className='pb-figure'>
        <div className='pb-figure__label'>Paid</div>
        <div className='pb-figure__value'>{money(detail.paidTotal)}</div>
      </div>
      <div className='pb-figure pb-figure--owed'>
        <div className='pb-figure__label'>Owed</div>
        <div className='pb-figure__value'>{money(detail.balance)}</div>
      </div>
    </div>

    {detail.entries.length === 0 ? (
      <p className='lk-muted'>Nothing in their piggy bank.</p>
    ) : (
      <ul className='pb-entries'>
        {detail.entries.map((entry) => (
          <li key={`${entry.type}-${entry.id}`} className='pb-entry'>
            <div className='pb-entry__top'>
              <span className={`pb-type pb-type--${entry.type.toLowerCase()}`}>{TYPE_LABELS[entry.type]}</span>
              <span className='pb-entry__title'>{entry.title}</span>
              <span className={`pb-entry__value${entry.isPayment ? ' pb-entry__value--out' : ''}`}>
                {entry.isPayment ? '−' : ''}
                {money(entry.value)}
              </span>
            </div>
            <div className='pb-entry__meta'>
              {shortDate(entry.date)}
              {entry.type === 'Referral' ? (
                <ReferralOrigin entry={entry} />
              ) : (
                <>
                  {entry.createdBy && (
                    <>
                      {' '}
                      · {entry.isPayment ? 'Recorded' : 'Given'} by {entry.createdBy}
                    </>
                  )}
                  {entry.itemDescription && <div>{entry.itemDescription}</div>}
                  {entry.notes && <div className='pb-entry__notes'>{entry.notes}</div>}
                </>
              )}
            </div>
            {entry.type !== 'Referral' && (
              <button type='button' className='pb-entry__remove' onClick={() => onRemove(entry)}>
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
    )}
  </>
)

/** Where a referral was raised, the enquiry it made and the job(s) that came of it. Each opens in a new tab. */
const ReferralOrigin = ({ entry }: { entry: PiggyBankEntry }) => (
  <div className='pb-origin'>
    {entry.fromJobId && entry.fromJobRef && (
      <div>
        Raised from job{' '}
        <button type='button' className='pb-link' onClick={() => openInNewTab(`/jobs?id=${entry.fromJobId}`)}>
          {entry.fromJobRef}
        </button>
        {entry.fromJobClient && <> ({entry.fromJobClient})</>}
      </div>
    )}
    {entry.enquiryId && entry.enquiryRef && (
      <div>
        Enquiry{' '}
        <button type='button' className='pb-link' onClick={() => openInNewTab(`/enquiries?id=${entry.enquiryId}`)}>
          {entry.enquiryRef}
        </button>
      </div>
    )}
    {entry.resultingJobs.length > 0 && (
      <div>
        Became job{entry.resultingJobs.length === 1 ? '' : 's'}{' '}
        {entry.resultingJobs.map((job, i) => (
          <span key={job.jobId}>
            {i > 0 && ', '}
            <button type='button' className='pb-link' onClick={() => openInNewTab(`/jobs?id=${job.jobId}`)}>
              {job.ref}
            </button>{' '}
            ({shortDate(job.createdDate)})
          </span>
        ))}
      </div>
    )}
  </div>
)

// --- Record a payment ------------------------------------------------------------

const RecordPaymentDialog = ({
  detail,
  onClose,
  onSaved,
}: {
  detail: PiggyBankUserDetail
  onClose: () => void
  onSaved: (updated: PiggyBankUserDetail) => void
}) => {
  const { showToast } = useToast()
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  // Starts at everything they're owed - the usual case is paying them up.
  const [amount, setAmount] = useState<number | null>(detail.balance)
  const [paidDate, setPaidDate] = useState<Date | null>(today)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const tooMuch = amount !== null && amount > detail.balance
  const canSave = amount !== null && amount > 0 && !tooMuch && !!paidDate && paidDate <= today && !saving

  const save = async () => {
    if (amount === null || !paidDate) return
    setSaving(true)
    try {
      const updated = await recordPiggyBankPayment(detail.employeeId, {
        amount,
        paidDate: isoDay(paidDate),
        notes,
      })
      showToast(`${money(amount)} paid to ${detail.employeeName}`, 'success')
      onSaved(updated)
    } catch (error) {
      showToast(getApiErrorDetails(error, "The payment couldn't be recorded.").message, 'error')
      setSaving(false)
    }
  }

  return (
    <Dialog title='Record payment' onClose={onClose} width='520px'>
      <div className='pb-form'>
        <label className='pb-form__label'>Person</label>
        <div className='pb-form__fixed'>
          {detail.employeeName} · owed {money(detail.balance)}
        </div>

        <label className='pb-form__label'>Amount paid (£) *</label>
        <NumericTextBox
          value={amount}
          onChange={(e) => setAmount(e.value)}
          format='n2'
          min={0}
          max={detail.balance}
          step={1}
        />
        {tooMuch && <p className='lk-hint pb-form__hint pb-form__error'>They're only owed {money(detail.balance)}.</p>}

        <label className='pb-form__label'>Date paid *</label>
        <DatePicker value={paidDate} onChange={(e) => setPaidDate(e.value)} max={today} format='dd/MM/yyyy' />

        <label className='pb-form__label'>Notes</label>
        <TextArea
          value={notes}
          onChange={(e) => setNotes(String(e.value ?? ''))}
          rows={3}
          maxLength={1000}
          placeholder='e.g. Paid with October wages'
        />
      </div>
      <DialogActionsBar>
        <Button onClick={onClose}>Cancel</Button>
        <Button themeColor='primary' onClick={save} disabled={!canSave}>
          {saving ? 'Saving…' : 'Record payment'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  )
}

// --- Give an item ----------------------------------------------------------------

const GiveItemDialog = ({
  employee: fixedEmployee,
  items,
  onClose,
  onSaved,
}: {
  employee: dropDown | null
  items: PiggyBankItem[]
  onClose: () => void
  onSaved: (updated: PiggyBankUserDetail) => void
}) => {
  const { showToast } = useToast()
  const [employee, setEmployee] = useState<dropDown | null>(fixedEmployee)
  const [employeeOptions, setEmployeeOptions] = useState<dropDown[]>([])
  const [item, setItem] = useState<PiggyBankItem | null>(null)
  const [value, setValue] = useState<number | null>(null)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const searchEmployees = useCallback(async (term: string) => {
    try {
      setEmployeeOptions(await getEmployeeDropdown(term))
    } catch {
      setEmployeeOptions([])
    }
  }, [])

  useEffect(() => {
    if (!fixedEmployee) searchEmployees('')
  }, [fixedEmployee, searchEmployees])

  const canSave = !!employee && !!item && value !== null && value > 0 && !saving

  const save = async () => {
    if (!employee || !item || value === null) return
    setSaving(true)
    try {
      const updated = await allocatePiggyBankItem(Number(employee.value), {
        piggyBankItemId: item.piggyBankItemId,
        value,
        notes,
      })
      showToast(`${item.name} (${money(value)}) given to ${employee.text}`, 'success')
      onSaved(updated)
    } catch (error) {
      showToast(getApiErrorDetails(error, "The item couldn't be given.").message, 'error')
      setSaving(false)
    }
  }

  return (
    <Dialog title='Give item' onClose={onClose} width='520px'>
      <div className='pb-form'>
        <label className='pb-form__label'>Person *</label>
        {fixedEmployee ? (
          <div className='pb-form__fixed'>{fixedEmployee.text}</div>
        ) : (
          <ComboBox
            data={employeeOptions}
            textField='text'
            dataItemKey='value'
            value={employee}
            onChange={(e) => setEmployee(e.value)}
            filterable
            onFilterChange={(e) => searchEmployees(e.filter.value)}
            placeholder='Search for someone…'
          />
        )}

        <label className='pb-form__label'>Item *</label>
        <DropDownList
          data={items}
          textField='name'
          dataItemKey='piggyBankItemId'
          value={item}
          onChange={(e) => {
            setItem(e.value)
            setValue(e.value ? e.value.value : null)
          }}
          defaultItem={{ piggyBankItemId: 0, name: 'Choose an item…' }}
        />
        {item?.description && <p className='lk-hint pb-form__hint'>{item.description}</p>}

        <label className='pb-form__label'>Value (£) *</label>
        <NumericTextBox value={value} onChange={(e) => setValue(e.value)} format='n2' min={0} step={1} />

        <label className='pb-form__label'>Notes</label>
        <TextArea value={notes} onChange={(e) => setNotes(String(e.value ?? ''))} rows={3} maxLength={1000} />
      </div>
      <DialogActionsBar>
        <Button onClick={onClose}>Cancel</Button>
        <Button themeColor='primary' onClick={save} disabled={!canSave}>
          {saving ? 'Saving…' : 'Give item'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  )
}

// --- Add / edit a catalogue item -----------------------------------------------------

const ItemDialog = ({
  item,
  onClose,
  onSaved,
}: {
  item: PiggyBankItem | null
  onClose: () => void
  onSaved: (name: string) => void
}) => {
  const { showToast } = useToast()
  const [name, setName] = useState(item?.name ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [value, setValue] = useState<number | null>(item?.value ?? 0)
  const [saving, setSaving] = useState(false)

  const canSave = name.trim().length > 0 && value !== null && value >= 0 && !saving

  const save = async () => {
    if (value === null) return
    setSaving(true)
    try {
      const input = { name: name.trim(), description: description.trim(), value }
      if (item) {
        await updatePiggyBankItem(item.piggyBankItemId, input)
      } else {
        await createPiggyBankItem(input)
      }
      onSaved(input.name)
    } catch (error) {
      showToast(getApiErrorDetails(error, "The item couldn't be saved.").message, 'error')
      setSaving(false)
    }
  }

  return (
    <Dialog title={item ? 'Edit item' : 'Add item'} onClose={onClose} width='520px'>
      <div className='pb-form'>
        <label className='pb-form__label'>Name *</label>
        <Input value={name} onChange={(e) => setName(String(e.value ?? ''))} maxLength={100} />

        <label className='pb-form__label'>Description</label>
        <TextArea value={description} onChange={(e) => setDescription(String(e.value ?? ''))} rows={3} maxLength={1000} />

        <label className='pb-form__label'>Value (£) *</label>
        <NumericTextBox value={value} onChange={(e) => setValue(e.value)} format='n2' min={0} step={1} />
        {item && item.allocationCount > 0 && (
          <p className='lk-hint pb-form__hint'>
            Changing the value only affects ones given from now on. The {plural(item.allocationCount, 'one')} already
            given keep what they were given.
          </p>
        )}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose}>Cancel</Button>
        <Button themeColor='primary' onClick={save} disabled={!canSave}>
          {saving ? 'Saving…' : item ? 'Save' : 'Add item'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  )
}

export default PiggyBankAdmin
